import { create } from "zustand";
/**
 * Global guided tour state management store
 *
 * Manages the state of onboarding/guided tours shown to users.
 * Tracks whether a tour is currently active and which step user is on.
 * Used by react-joyride library for step-by-step guided tours.
 *
 * Tour Flow:
 * 1. startTour() opens tour and resets to step 0
 * 2. nextStep() / previousStep() navigate between steps
 * 3. closeTour() hides tour overlay but doesn't reset step
 *
 * @returns {Object} Tour store with state and actions
 */
const useTourStore = create((set) => ({
  /**
   * Whether the tour overlay is currently visible
   * @type {boolean}
   */
  isTourOpen: false,

  /**
   * Current step index in the tour sequence
   * Used by react-joyride to render appropriate tooltip/highlight
   * @type {number}
   */
  stepIndex: 0,

  /**
   * Move to next step in the tour
   *
   * Increments stepIndex by 1
   * Does not wrap around at end of tour
   */
  nextStep: () =>
    set((state) => ({
      stepIndex: state.stepIndex + 1,
    })),

  /**
   * Move to previous step in the tour
   *
   * Decrements stepIndex by 1
   * Does not wrap around at beginning of tour
   */
  previousStep: () =>
    set((state) => ({
      stepIndex: state.stepIndex - 1,
    })),

  /**
   * Start the tour from the beginning
   *
   * Opens tour overlay and resets stepIndex to 0
   */
  startTour: () =>
    set({
      isTourOpen: true,
      stepIndex: 0,
    }),

  /**
   * Close the tour overlay
   *
   * Hides tour but preserves stepIndex (useful for resuming)
   */
  closeTour: () =>
    set({
      isTourOpen: false,
    }),
}));

/**
 * Global Zustand tour state store for guided onboarding
 * 
 * Usage:
 *   import useTourStore from './tourStore'
 *   const { isTourOpen, stepIndex, startTour, closeTour, nextStep } = useTourStore()
 */
export default useTourStore;
